import * as ko from "knockout";
import {_} from 'lodash';
import {global, lm} from "../../../appContext";
import pubsub from "../../../pubsub";
import {ICloneDialogOptions} from "../../../clone/index";
import {IMessage, IRuleset, ISearchParams, ICompanySyncstep} from "../../../index";
import {MAX_RESULTS_PER_PAGE} from "../../../constants";
import {ERROR_MESSAGE, REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../../constants/Language";
import {getHash} from "../../../constants/RouteDefinitions";
import {createErrorMessage, createErrorString} from "../../../util/misc";
import {nsClone} from "../../../clone/fn";
import targetOrg = nsClone.targetOrg;
import {
    copyRulesetsWithApis,
    ICopyRulesetResult,
    topologicalCopyOrder,
} from "../../ruleset/copy-wizard/copyRulesetGraph";
import {RulesetNameConflictRowVm} from "../../ruleset/copy-wizard/rulesetNameConflictRow";
import type {IStepperStep} from "../../ruleset/copy-wizard/sub/stepper-host";
import {nsMessageFlowClone} from "../../../clone/nsMessageFlowClone";
import {nsSyncstep} from "../../sync-steps/fn";
import {buildOrderedPreviewRows, collectRulesetClosureForMessageFlows, ICopyPreviewRow} from "./copyMessageFlowPlan";
import {isRulesetNameValidForCopyWizard} from "../../ruleset/copy-wizard/copyWizardStrictNames";
import {MessageFlowNameConflictRowVm} from "./messageFlowNameConflictRow";
import mainHtml from './main.html?raw';

export const template = mainHtml;

const PREFIX = "messageflow.copy.wizard";

export enum WizardInternalStep {
    LoadSource = 1,
    PickTargetOrg = 2,
    LoadTarget = 3,
    ResolveConflicts = 4,
    Process = 5,
    Receipt = 6,
}

export interface IMessageFlowCopyWizardParams {
    dialog: any;
    item?: IMessage;
    selectedObjects?: IMessage[];
    options?: ICloneDialogOptions;
    requestParams?: ISearchParams;
}

export type IMessageFlowCopyRow =
    | {kind: "messageflow"; vm: MessageFlowNameConflictRowVm}
    | {kind: "ruleset"; vm: RulesetNameConflictRowVm};

export type ICopyMessageFlowApiResult =
    | {sourceId: number; success: true; targetMessage: IMessage}
    | {sourceId: number; success: false; error: string};

export class viewModel {
    readonly internalStep = ko.observable<WizardInternalStep>(WizardInternalStep.LoadSource);
    readonly messageData = ko.observable(null);
    readonly headingKey = ko.observable(`${PREFIX}.loadsource`);
    readonly descriptionKey = ko.observable(`${PREFIX}.loadsource.description`);
    readonly showStepper = ko.observable(false);
    readonly stepperCurrentIndex = ko.observable(0);
    readonly stepperSteps: ReadonlyArray<IStepperStep> = [
        {
            labelKey: `${PREFIX}.stepper.picktargetorg`,
            descriptionKey: `${PREFIX}.stepper.picktargetorg.description`,
        },
        {
            labelKey: `${PREFIX}.stepper.resolveconflicts`,
            descriptionKey: `${PREFIX}.stepper.resolveconflicts.description`,
        },
        {
            labelKey: `${PREFIX}.stepper.receipt`,
            descriptionKey: `${PREFIX}.stepper.receipt.description`,
        },
    ];

    readonly srcOrg = global.login.selectedOrganization;
    readonly organizations = global.login.organizations;
    readonly trgOrg = targetOrg;

    readonly rootFlowsOrdered = ko.observableArray<IMessage>([]);
    /** When false, the message flow’s main ruleset (and sub-rulesets) are not copied. */
    readonly copyMainRulesets = ko.observable(true);
    readonly uniqueRulesets = ko.observableArray<IRuleset>([]);
    readonly orderedPreviewRows = ko.observableArray<ICopyPreviewRow>([]);
    readonly combinedConflictRows = ko.observableArray<IMessageFlowCopyRow>([]);
    readonly rulesetCopyResults = ko.observableArray<ICopyRulesetResult>([]);
    readonly messageCopyResults = ko.observableArray<ICopyMessageFlowApiResult>([]);

    readonly showPrevious = ko.observable(false);
    readonly showNext = ko.observable(false);
    readonly showCancel = ko.observable(true);
    readonly canGoPrevious = ko.pureComputed(
        () => this.internalStep() === WizardInternalStep.ResolveConflicts,
    );
    readonly canGoNext = ko.pureComputed((): boolean => {
        switch (this.internalStep()) {
            case WizardInternalStep.PickTargetOrg:
                return !!this.trgOrg()?.organizationid;
            case WizardInternalStep.ResolveConflicts:
                return this.canAdvanceFromConflictStep();
            default:
                return false;
        }
    });
    readonly nextLabelKey = ko.observable("next");
    readonly nextButtonClassMap = ko.observable<Record<string, boolean>>({
        "btn-primary": true,
        "btn-success": false,
    });
    readonly cancelLabelKey = ko.observable("cancel");
    readonly cancelIcon = ko.observable("fa-ban");

    private sourceMessagesById = new Map<number, IMessage>();
    private sourceRulesetsById = new Map<number, IRuleset>();
    private targetRulesetsList: IRuleset[] = [];
    private targetMessagesList: IMessage[] = [];
    private activeSearchRequest: ReturnType<typeof global.getPromiseRequest> | null = null;

    constructor(private readonly params: IMessageFlowCopyWizardParams) {
        if (!this.trgOrg() && this.organizations().length) {
            const src = this.srcOrg();
            if (src) {
                this.trgOrg(src);
            }
        }
        this.internalStep.subscribe((s) => this.onStepChanged(s));
        this.copyMainRulesets.subscribe(() => this.rebuildMessageFlowRulesetPlan());
        this.onStepChanged(this.internalStep());
    }

    readonly canAdvanceFromConflictStep = ko.pureComputed((): boolean => {
        if (this.internalStep() !== WizardInternalStep.ResolveConflicts) {
            return false;
        }
        this.combinedConflictRows().forEach((r) => {
            r.vm.draftName();
        });
        return this.combinedConflictRows().every((r) => !r.vm.hasBlockingConflict());
    });

    private onStepChanged(step: WizardInternalStep) {
        this.updateChrome(step);
        switch (step) {
            case WizardInternalStep.LoadSource:
                this.runLoadSource();
                break;
            case WizardInternalStep.PickTargetOrg:
                break;
            case WizardInternalStep.LoadTarget:
                this.runLoadTarget();
                break;
            case WizardInternalStep.ResolveConflicts:
                this.prepareConflictRows();
                break;
            case WizardInternalStep.Process:
                this.runProcess();
                break;
            case WizardInternalStep.Receipt:
                break;
            default:
                break;
        }
    }

    private updateChrome(step: WizardInternalStep) {
        const keyOf = (k: string) => `${PREFIX}.${k}`;
        const showPnNav = step !== WizardInternalStep.Receipt;
        this.showPrevious(showPnNav);
        this.showNext(showPnNav);
        switch (step) {
            case WizardInternalStep.LoadSource:
                this.headingKey(keyOf("loadsource"));
                this.descriptionKey(keyOf("loadsource.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.PickTargetOrg:
                this.headingKey(keyOf("itemslist"));
                this.descriptionKey(keyOf("itemslist.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(0);
                this.showCancel(true);
                this.nextLabelKey("next");
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.LoadTarget:
                this.headingKey(keyOf("loadtarget"));
                this.descriptionKey(keyOf("loadtarget.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.ResolveConflicts:
                this.headingKey(keyOf("resolveconflicts"));
                this.descriptionKey(keyOf("resolveconflicts.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(1);
                this.showCancel(true);
                this.nextLabelKey(keyOf("submit"));
                this.nextButtonClassMap({"btn-primary": false, "btn-success": true});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.Process:
                this.headingKey(keyOf("process"));
                this.descriptionKey(keyOf("process.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.Receipt:
                this.headingKey(keyOf("receipt"));
                this.descriptionKey(keyOf("receipt.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(2);
                this.showCancel(true);
                this.cancelLabelKey("close");
                this.cancelIcon("fa-times");
                break;
            default:
                break;
        }
    }

    private rebuildMessageFlowRulesetPlan() {
        const roots = this.rootFlowsOrdered();
        if (!roots.length) {
            return;
        }
        const includeMain = this.copyMainRulesets();
        const unique = collectRulesetClosureForMessageFlows(roots, this.sourceRulesetsById, {
            includeMessageMainRuleset: includeMain,
        });
        this.uniqueRulesets(unique);
        this.orderedPreviewRows(buildOrderedPreviewRows(roots, unique, includeMain));
        if (this.internalStep() === WizardInternalStep.ResolveConflicts) {
            this.prepareConflictRows();
        }
    }

    private getRootFlows(): IMessage[] {
        if (this.params.selectedObjects?.length) {
            return this.params.selectedObjects;
        }
        if (this.params.item) {
            return [this.params.item];
        }
        return [];
    }

    private runLoadSource() {
        this.setDialogLocked(false);
        this.messageData({message: REQUEST_MESSAGE});
        const orgnr = this.srcOrg()?.organizationid;
        if (!orgnr) {
            return;
        }
        const reqMsg = global.getPromiseRequest("searchmessage");
        const reqRs = global.getPromiseRequest("searchruleset");
        this.activeSearchRequest = reqMsg;
        Promise.all([
            reqMsg.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
            reqRs.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
        ])
            .then(([messages, rulesets]: [IMessage[], IRuleset[]]) => {
                this.sourceMessagesById = new Map(messages.map((m) => [m.id, m]));
                this.sourceRulesetsById = new Map(rulesets.map((r) => [r.id, r]));
                const roots = this.getRootFlows()
                    .map((m) => this.sourceMessagesById.get(m.id) || m)
                    .filter(Boolean) as IMessage[];
                this.rootFlowsOrdered(roots);
                this.rebuildMessageFlowRulesetPlan();
                this.messageData(null);
                this.internalStep(WizardInternalStep.PickTargetOrg);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private runLoadTarget() {
        this.messageData({message: REQUEST_MESSAGE});
        const orgnr = this.trgOrg()?.organizationid;
        if (!orgnr) {
            this.messageData(createErrorMessage("missing organization"));
            return;
        }
        const reqMsg = global.getPromiseRequest("searchmessage");
        const reqRs = global.getPromiseRequest("searchruleset");
        this.activeSearchRequest = reqMsg;
        Promise.all([
            reqMsg.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
            reqRs.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
        ])
            .then(([msgData, rsData]: [IMessage[], IRuleset[]]) => {
                this.targetMessagesList = msgData;
                this.targetRulesetsList = rsData;
                this.messageData(null);
                this.internalStep(WizardInternalStep.ResolveConflicts);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private captureMessageFlowConflictDraftNames(): {
        messageflow: Map<number, string>;
        ruleset: Map<number, string>;
    } {
        const messageflow = new Map<number, string>();
        const ruleset = new Map<number, string>();
        for (const row of this.combinedConflictRows()) {
            if (row.kind === "messageflow") {
                messageflow.set(row.vm.message.id, row.vm.draftName());
            } else {
                ruleset.set(row.vm.ruleset.id, row.vm.draftName());
            }
        }
        return {messageflow, ruleset};
    }

    private prepareConflictRows() {
        const preservedDraftNames = this.captureMessageFlowConflictDraftNames();
        const previewTemplate = this.orderedPreviewRows();
        const roots = this.rootFlowsOrdered();
        const unique = this.uniqueRulesets();

        const messageNameCounts = new Map<string, number>();
        for (const m of roots) {
            const key = m.name.trim();
            messageNameCounts.set(key, (messageNameCounts.get(key) ?? 0) + 1);
        }
        const rulesetNameCounts = new Map<string, number>();
        for (const r of unique) {
            const key = r.name.trim();
            rulesetNameCounts.set(key, (rulesetNameCounts.get(key) ?? 0) + 1);
        }

        const built: IMessageFlowCopyRow[] = [];
        for (const row of previewTemplate) {
            if (row.kind === "messageflow" && row.message) {
                const m = row.message;
                const batchDup = (messageNameCounts.get(m.name.trim()) ?? 0) > 1;
                const messageVm = new MessageFlowNameConflictRowVm(
                    m,
                    () => this.targetMessagesList,
                    () =>
                        built
                            .filter(
                                (r): r is {kind: "messageflow"; vm: MessageFlowNameConflictRowVm} =>
                                    r.kind === "messageflow" && r.vm.message.id !== m.id,
                            )
                            .map((r) => r.vm.draftName().trim()),
                    batchDup,
                );
                const preservedMessageName = preservedDraftNames.messageflow.get(m.id);
                if (preservedMessageName !== undefined) {
                    messageVm.draftName(preservedMessageName);
                }
                built.push({kind: "messageflow", vm: messageVm});
            } else if (row.kind === "ruleset" && row.ruleset) {
                const rs = row.ruleset;
                const batchDup = (rulesetNameCounts.get(rs.name.trim()) ?? 0) > 1;
                const rulesetVm = new RulesetNameConflictRowVm(
                    rs,
                    () => this.targetRulesetsList,
                    () =>
                        built
                            .filter(
                                (r): r is {kind: "ruleset"; vm: RulesetNameConflictRowVm} =>
                                    r.kind === "ruleset" && r.vm.ruleset.id !== rs.id,
                            )
                            .map((r) => r.vm.draftName().trim()),
                    batchDup,
                    isRulesetNameValidForCopyWizard,
                );
                const preservedRulesetName = preservedDraftNames.ruleset.get(rs.id);
                if (preservedRulesetName !== undefined) {
                    rulesetVm.draftName(preservedRulesetName);
                }
                built.push({kind: "ruleset", vm: rulesetVm});
            }
        }
        this.combinedConflictRows(built);
    }

    private runProcess() {
        this.setDialogLocked(true);
        this.messageData({message: UPDATING_MESSAGE});
        const trgOrgnr = this.trgOrg().organizationid;
        const rulesetNameMap = new Map<number, string>();
        const messageNameMap = new Map<number, string>();
        for (const row of this.combinedConflictRows()) {
            if (row.kind === "ruleset") {
                rulesetNameMap.set(row.vm.ruleset.id, row.vm.draftName().trim());
            } else {
                messageNameMap.set(row.vm.message.id, row.vm.draftName().trim());
            }
        }

        nsSyncstep
            .getAll({orgnr: trgOrgnr})
            .then((syncData: ICompanySyncstep[]) => {
                if (!syncData.length) {
                    throw ERROR_MESSAGE;
                }
                const sorted = _.sortBy(syncData, "order");
                const last = _.last(sorted)!;
                const syncstepid = last.syncstep.id;
                const syncstepname = last.syncstep.name;
                const ordered = topologicalCopyOrder(this.uniqueRulesets());
                return copyRulesetsWithApis(ordered, trgOrgnr, rulesetNameMap).then((rsResults) => ({
                    rsResults,
                    syncstepid,
                    syncstepname,
                }));
            })
            .then(({rsResults, syncstepid, syncstepname}) => {
                this.rulesetCopyResults(rsResults);
                const rulesetSourceToTarget = new Map<number, IRuleset>();
                for (const r of rsResults) {
                    if (r.success) {
                        rulesetSourceToTarget.set(r.sourceId, r.targetRuleset);
                    }
                }
                const msgOut: ICopyMessageFlowApiResult[] = [];
                const flows = this.rootFlowsOrdered();
                const runNext = (idx: number): Promise<void> => {
                    if (idx >= flows.length) {
                        return Promise.resolve();
                    }
                    const srcFlow = flows[idx];
                    if (!srcFlow) {
                        return runNext(idx + 1);
                    }
                    const chosenName = messageNameMap.get(srcFlow.id) ?? srcFlow.name;
                    const cpy: IMessage = _.cloneDeep(
                        this.sourceMessagesById.get(srcFlow.id) || srcFlow,
                    );
                    cpy.id = 0;
                    cpy.companyid = trgOrgnr;
                    cpy.active = false as any;
                    cpy.name = chosenName;
                    cpy.syncstepid = syncstepid;
                    cpy.syncstepname = syncstepname;
                    if (!this.copyMainRulesets()) {
                        cpy.rulesetid = 0;
                        cpy.rulesetname = "";
                    } else if (cpy.rulesetid) {
                        const mapped = rulesetSourceToTarget.get(cpy.rulesetid);
                        if (!mapped) {
                            const rsFailed = rsResults.find(
                                (x): x is Extract<ICopyRulesetResult, {success: false}> =>
                                    !x.success && x.sourceId === cpy.rulesetid,
                            );
                            const errText = (rsFailed ? rsFailed.error : lm.t("messageflow.copy.wizard.error.rulesetmissing")) ?? '';
                            msgOut.push({sourceId: srcFlow.id, success: false, error: errText});
                            return runNext(idx + 1);
                        }
                        cpy.rulesetid = mapped.id;
                        cpy.rulesetname = mapped.name;
                    }
                    return nsMessageFlowClone
                        .persistPreparedCopy(cpy, trgOrgnr)
                        .then((created) => {
                            msgOut.push({sourceId: srcFlow.id, success: true, targetMessage: created});
                        })
                        .catch((reason: string) => {
                            msgOut.push({
                                sourceId: srcFlow.id,
                                success: false,
                                error: createErrorString(reason, "_blank"),
                            });
                        })
                        .then(() => runNext(idx + 1));
                };
                return runNext(0).then(() => msgOut);
            })
            .then((msgResults: ICopyMessageFlowApiResult[]) => {
                this.messageCopyResults(msgResults);
                global.cacheManager.removeCache("getruleset");
                global.cacheManager.removeCache("searchruleset");
                global.cacheManager.removeCache("getehubprocess");
                global.cacheManager.removeCache("searchmessage");
                global.cacheManager.removeCache("getmessage");
                if (msgResults.some((r) => r.success)) {
                    pubsub.trigger("messagetemplate.copy.success");
                }
                if (this.rulesetCopyResults().some((r) => r.success)) {
                    pubsub.trigger("ruleset.copy.success");
                }
                if (
                    this.srcOrg()?.organizationid === trgOrgnr &&
                    (msgResults.some((r) => r.success) || this.rulesetCopyResults().some((r) => r.success))
                ) {
                    pubsub.trigger("search.needs.refresh");
                }
                this.messageData(null);
                this.setDialogLocked(false);
                this.internalStep(WizardInternalStep.Receipt);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.setDialogLocked(false);
            });
    }

    private setDialogLocked(locked: boolean) {
        const d = this.params.dialog;
        if (d?.closeable && ko.isObservable(d.closeable)) {
            d.closeable(!locked);
        }
        if (d?.isClosable && ko.isObservable(d.isClosable)) {
            d.isClosable(!locked);
        }
    }

    rulesetHref = (rs: IRuleset): string => getHash("rulesetdetails", {id: rs.id, orgnr: rs.companyid}) ?? '';

    messageHref = (m: IMessage): string => getHash("messageflowdetails", {id: m.id, orgnr: m.companyid}) ?? '';

    onPreviousClick = () => {
        if (this.internalStep() === WizardInternalStep.ResolveConflicts) {
            this.internalStep(WizardInternalStep.PickTargetOrg);
        }
    };

    onNextClick = () => {
        switch (this.internalStep()) {
            case WizardInternalStep.PickTargetOrg:
                this.internalStep(WizardInternalStep.LoadTarget);
                break;
            case WizardInternalStep.ResolveConflicts:
                this.internalStep(WizardInternalStep.Process);
                break;
            default:
                break;
        }
    };

    onCancelClick = () => {
        this.closeDialog();
    };

    private closeDialog() {
        const d = this.params.dialog;
        if (typeof d?.doClose === "function") {
            d.doClose();
        } else {
            pubsub.trigger("close.dialog", d);
        }
    }

    rulesetResultForSourceId(id: number): ICopyRulesetResult | undefined {
        return this.rulesetCopyResults().find((r) => r.sourceId === id);
    }

    messageResultForSourceId(id: number): ICopyMessageFlowApiResult | undefined {
        return this.messageCopyResults().find((r) => r.sourceId === id);
    }

    readonly receiptRows = ko.pureComputed(() => {
        const rows: Array<{
            preview: ICopyPreviewRow;
            rulesetResult?: ICopyRulesetResult;
            messageResult?: ICopyMessageFlowApiResult;
        }> = [];
        for (const p of this.orderedPreviewRows()) {
            if (p.kind === "messageflow" && p.message) {
                const messageResult = this.messageResultForSourceId(p.message.id);
                if (messageResult) {
                    rows.push({preview: p, messageResult});
                }
            } else if (p.kind === "ruleset" && p.ruleset) {
                const rulesetResult = this.rulesetResultForSourceId(p.ruleset.id);
                if (rulesetResult) {
                    rows.push({preview: p, rulesetResult});
                }
            }
        }
        return rows;
    });

    readonly receiptErrorCount = ko.pureComputed(() => {
        const rs = this.rulesetCopyResults().filter((r) => !r.success).length;
        const mf = this.messageCopyResults().filter((r) => !r.success).length;
        return rs + mf;
    });

    readonly receiptHasErrors = ko.pureComputed(() => this.receiptErrorCount() > 0);

    receiptBannerKey = ko.pureComputed(() =>
        this.receiptHasErrors() ? `${PREFIX}.receipt.errorsoccured|n:${this.receiptErrorCount()}` : null,
    );

    dispose() {
        this.activeSearchRequest?.abort();
    }
}
